import React, { useRef, useMemo, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import DataObjectIcon from "@mui/icons-material/DataObject";
import Tooltip from "@mui/material/Tooltip";
import { brand, gray } from "../../theme/themePrimitives";
import codeIcon from "../../assets/code.svg";
import editPencilIcon from "../../assets/edit-pencil.svg";
import { EmailTemplateTypeEnum } from "../../services/api/types/emailTemplates";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";

export type QuillEditorProps = {
  value: string;
  onChange: (val: string) => void;
  height?: number;
  /** Optional merge fields to make available for insertion */
  mergeFields?: Array<{ label: string; value: string }>;
  disabled?: boolean;
  mode?: "template" | "custom";
  type?: EmailTemplateTypeEnum | "";
};

const QuillEditor: React.FC<QuillEditorProps> = ({
  value,
  onChange,
  height = 200,
  mergeFields = [],
  disabled = false,
  mode,
  type = "",
}) => {
  const quillRef = useRef<any>(null);
  const isCustomMode = mode === "custom";
  const isFileHubSubscriptionType =
    type === EmailTemplateTypeEnum.FILEHUB_SUBSCRIPTION;

  const availableMergeFields =
    mergeFields && mergeFields.length > 0
      ? mergeFields
      : [
          { label: "Portal name", value: "portal_name" },
          { label: "First name", value: "first_name" },
          { label: "Last name", value: "last_name" },
          { label: "Logo", value: "logo" },
          { label: "Link", value: "link" },
          { label: "Assignment list", value: "assignment_list" },
          { label: "Overdue users", value: "overdue_users" },
          { label: "FileHub name", value: "filehub_name" },
        ];

  const DISABLED_FIELDS_BY_TYPE: Partial<
    Record<EmailTemplateTypeEnum, string[]>
  > = {
    [EmailTemplateTypeEnum.INVITE_USER]: ["overdue_users"],
    [EmailTemplateTypeEnum.FORGOT_PASSWORD]: [
      "assignment_list",
      "overdue_users",
    ],
    [EmailTemplateTypeEnum.NEW_ASSIGNMENT]: ["overdue_users"],
    [EmailTemplateTypeEnum.USER_REMINDER]: ["overdue_users"],
    [EmailTemplateTypeEnum.MANAGER_REMINDER]: ["assignment_list"],
    [EmailTemplateTypeEnum.FILEHUB_SUBSCRIPTION]: [
      "assignment_list",
      "overdue_users",
    ],
  };

  const DISABLED_FIELDS_CUSTOM_MODE = ["assignment_list", "overdue_users"];

  const isDisabledMergeField = new Set<string>([
    ...(isCustomMode ? DISABLED_FIELDS_CUSTOM_MODE : []),
    ...(type
      ? (DISABLED_FIELDS_BY_TYPE[type as EmailTemplateTypeEnum] ?? [])
      : []),
    // filehub_name only allowed for FileHub subscription
    ...(isFileHubSubscriptionType ? [] : ["filehub_name"]),
  ]);

  // Register custom embed blots for merge fields and HTML blocks
  useMemo(() => {
    const Q: any = (ReactQuill as any).Quill;
    if (!Q) return;
    const InlineEmbed = Q.import("blots/embed");
    const BlockEmbed = Q.import("blots/block/embed");

    // Merge field blot (inline, non-editable token)
    class MF extends InlineEmbed {
      static blotName = "mergefield";
      static tagName = "span";
      static className = "ql-mergefield";

      static create(value: string) {
        const node: any = super.create();
        node.setAttribute("data-mergefield", value);
        node.setAttribute("contenteditable", "false");
        node.innerText = `{{${value}}}`;
        return node;
      }

      static value(node: any) {
        return node.getAttribute("data-mergefield");
      }
    }

    class HtmlBlock extends BlockEmbed {
      static blotName = "htmlblock";
      static tagName = "div";
      static className = "ql-htmlblock";

      static create(value: string) {
        const node = super.create() as HTMLElement;
        node.setAttribute("data-html", value);
        node.setAttribute("contenteditable", "false");
        node.innerHTML = `<div class="ql-htmlblock-header"><div class="ql-htmlblock-title"><img src="${codeIcon}" alt="code" class="ql-htmlblock-title-icon" /><span>Custom HTML block</span></div><div class="ql-htmlblock-actions"><button type="button" data-htmlblock-action="edit" class="ql-htmlblock-btn"><img src="${editPencilIcon}" alt="edit" /></button><button type="button" data-htmlblock-action="delete" class="ql-htmlblock-btn"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button></div></div><div class="ql-htmlblock-content">${value}</div>`;
        return node;
      }

      static value(node: HTMLElement) {
        return node.getAttribute("data-html") || "";
      }
    }

    try {
      if (!Q.query || !Q.query("mergefield")) {
        Q.register(MF, true);
      }
    } catch (e) {
      // ignore if already registered
    }

    try {
      if (!Q.query || !Q.query("htmlblock")) {
        Q.register(HtmlBlock, true);
      }
    } catch (e) {
      // ignore if already registered
    }
  }, []);

  const [selectedMergeField, setSelectedMergeField] = useState<string>(() =>
    availableMergeFields[0] ? availableMergeFields[0].value : "",
  );

  const [isHtmlModalOpen, setIsHtmlModalOpen] = useState(false);
  const [htmlModalValue, setHtmlModalValue] = useState("");
  const [editingHtmlBlockIndex, setEditingHtmlBlockIndex] = useState<
    number | null
  >(null);

  const openHtmlModal = () => {
    setHtmlModalValue("");
    setEditingHtmlBlockIndex(null);
    setIsHtmlModalOpen(true);
  };

  const closeHtmlModal = () => {
    setIsHtmlModalOpen(false);
    setHtmlModalValue("");
    setEditingHtmlBlockIndex(null);
  };

  const insertHtmlBlock = () => {
    if (!htmlModalValue.trim()) {
      closeHtmlModal();
      return;
    }
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    if (editingHtmlBlockIndex !== null) {
      editor.deleteText(editingHtmlBlockIndex, 1, "user");
      editor.insertEmbed(
        editingHtmlBlockIndex,
        "htmlblock",
        htmlModalValue,
        "user",
      );
    } else {
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertEmbed(index, "htmlblock", htmlModalValue, "user");
      editor.setSelection(index + 1, 0);
    }
    editor.focus();
    closeHtmlModal();
  };

  const handleHtmlBlockAction = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const actionButton = target.closest(
      "[data-htmlblock-action]",
    ) as HTMLElement;
    if (!actionButton) return;

    const action = actionButton.getAttribute("data-htmlblock-action");
    const blockElement = actionButton.closest(".ql-htmlblock") as HTMLElement;
    if (!blockElement) return;

    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    const blot = (ReactQuill as any).Quill.find(blockElement);
    if (!blot) return;
    const index = editor.getIndex(blot);

    if (action === "edit") {
      const htmlValue = blockElement.getAttribute("data-html") || "";
      setHtmlModalValue(htmlValue);
      setEditingHtmlBlockIndex(index);
      setIsHtmlModalOpen(true);
    } else if (action === "delete") {
      editor.deleteText(index, 1, "user");
    }
  };

  const insertMergeField = (val?: string) => {
    const v = val ?? selectedMergeField;
    if (!v) return;
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = (range && range.index) || editor.getLength();
    // insert the embed at cursor
    editor.insertEmbed(index, "mergefield", v, "user");
    // Move cursor after the embed
    editor.setSelection(index + 1, 0);
    // focus editor
    editor.focus();
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      [{ header: 1 }, { header: 2 }],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ align: [] }],
      ["link"],
      // ["clean"],
    ],
  } as any;

  const quillFormats = [
    "mergefield",
    "htmlblock",
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "blockquote",
    "code-block",
    "list",
    "indent",
    "direction",
    "align",
    "link",
    // "image",
    // "video",
    // "formula",
  ];

  return (
    <Box>
      {/* Merge fields UI and Source toggle - MUI form controls */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        mb={1}
        flexWrap="wrap"
      >
        {availableMergeFields && availableMergeFields.length > 0 && (
          <>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                labelId="mergefield-select-label"
                id="mergefield-select"
                value={selectedMergeField}
                onChange={(e) =>
                  setSelectedMergeField(e.target.value as string)
                }
                disabled={disabled}
                sx={{ height: 36 }}
              >
                {availableMergeFields.map((m) => (
                  <MenuItem
                    key={m.value}
                    value={m.value}
                    disabled={isDisabledMergeField.has(m.value)}
                  >
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              color="info"
              onClick={() => insertMergeField()}
              size="small"
              disabled={disabled}
              sx={{
                px: 2,
                borderRadius: "12px",
                borderColor: `${brand[600]} !important`,
                color: brand[600],
                fontWeight: 600,
                height: 36,
              }}
            >
              Insert
            </Button>
          </>
        )}

        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 0.5 }}>
                Insert a raw HTML block
              </Typography>
              <Typography sx={{ fontSize: "13px", opacity: 0.9 }}>
                Renders exactly as written in the final email
              </Typography>
            </Box>
          }
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: gray[800],
                borderRadius: "8px",
                px: 2,
                py: 1.5,
                maxWidth: 280,
              },
            },
            arrow: {
              sx: {
                color: gray[800],
              },
            },
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={openHtmlModal}
            size="small"
            disabled={disabled}
            startIcon={<DataObjectIcon />}
            sx={{
              px: 2,
              borderRadius: "12px",
              fontWeight: 600,
              height: 36,
              borderColor: `${gray[300]} !important`,
              color: gray[700],
              cursor: "pointer",
              "&:hover": {
                borderColor: `${gray[400]} !important`,
                backgroundColor: gray[50],
              },
            }}
          >
            HTML block
          </Button>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />
      </Stack>

      <Box
        onClick={handleHtmlBlockAction}
        sx={{
          overflow: "hidden",
          border: `1px solid ${gray[200]}`,
          borderRadius: "12px",
          backgroundColor: disabled ? gray[50] : undefined,
        }}
      >
        <ReactQuill
          className={"quill-root" + (disabled ? " root-disabled" : "")}
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={quillModules}
          formats={quillFormats}
          readOnly={disabled}
        />
      </Box>

      {/* HTML Insert Modal */}
      <Modal
        open={isHtmlModalOpen}
        onClose={closeHtmlModal}
        aria-labelledby="html-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            maxWidth: "90vw",
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            padding: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: `1px solid ${gray[200]}`,
              pb: 2,
            }}
          >
            <Typography
              id="html-modal-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600, fontSize: "1.125rem" }}
            >
              Insert HTML Block
            </Typography>
            <IconButton
              sx={{
                border: "none",
              }}
              onClick={closeHtmlModal}
            >
              <CloseIcon sx={{ color: "#98A2B3" }} />
            </IconButton>
          </Box>
          <Box sx={{ py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Paste your HTML. Use tags to include dynamic content. Renders
              exactly as written.
            </Typography>
            <TextareaAutosize
              value={htmlModalValue}
              onChange={(e) => setHtmlModalValue(e.target.value)}
              minRows={10}
              maxRows={20}
              placeholder="<h1>...</h1>"
              style={{
                width: "100%",
                padding: "12px",
                fontFamily: "monospace",
                fontSize: "13px",
                lineHeight: 1.5,
                border: `1px solid ${gray[200]}`,
                borderRadius: "8px",
                outline: "none",
                resize: "vertical",
                backgroundColor: "white",
                color: gray[700],
                boxSizing: "border-box",
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              The HTML block is locked in the editor, it cannot be partially
              edited inline.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              borderTop: `1px solid ${gray[200]}`,
              pt: 2,
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={closeHtmlModal}
              fullWidth
              sx={{
                borderRadius: "8px",
                borderColor: `${gray[300]} !important`,
                color: gray[700],
                py: 1.25,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={insertHtmlBlock}
              disabled={!htmlModalValue.trim()}
              fullWidth
              sx={{
                borderRadius: "8px",
                backgroundColor: `${brand[600]} !important`,
                color: "white",
                py: 1.25,
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: `${brand[700]} !important`,
                },
              }}
            >
              Insert block
            </Button>
          </Box>
        </Box>
      </Modal>

      <style>{`
        .root-disabled {
          pointer-events: none;
          cursor: not-allowed;
        }

        .root-disabled .ql-toolbar.ql-snow * {
          opacity: 0.85;
        }

        .ql-editor {
          min-height: 200px;
        }

        .ql-mergefield {
          font-family: monospace;
          background: #eef2ff;
          color: #1f2937;
          padding: 2px 6px;
          border-radius: 4px;
          margin: 0 2px;
          display: inline-block;
          box-shadow: inset 0 -1px 0 rgba(0,0,0,0.06);
          font-size: 0.95em;
        }

        .ql-htmlblock {
          display: block;
          margin: 8px 0;
          border: 2px dashed ${gray[300]};
          border-radius: 8px;
          background-color: ${gray[50]};
          overflow: hidden;
        }

        .ql-htmlblock-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          background-color: ${gray[100]};
          border-bottom: 1px solid ${gray[200]};
        }

        .ql-htmlblock-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: ${brand[700]};
        }

        .ql-htmlblock-title-icon {
          width: 16px;
          height: 16px;
          filter: brightness(0) saturate(100%) invert(26%) sepia(68%) saturate(1856%) hue-rotate(243deg) brightness(87%) contrast(91%);
        }

        .ql-htmlblock-actions {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .ql-htmlblock-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          padding: 0;
          border: none;
          background: transparent;
          cursor: pointer;
          border-radius: 4px;
          color: ${brand[700]};
          transition: background-color 0.15s, color 0.15s;
        }

        .ql-htmlblock-btn img {
          filter: brightness(0) saturate(100%) invert(26%) sepia(68%) saturate(1856%) hue-rotate(243deg) brightness(87%) contrast(91%);
        }

        .ql-htmlblock-btn:hover {
          background-color: ${gray[200]};
          color: ${gray[700]};
        }

        .ql-htmlblock-btn img,
        .ql-htmlblock-btn svg {
          width: 16px;
          height: 16px;
        }

        .ql-htmlblock-content {
          padding: 12px;
        }

        /* Make the toolbar sticky and the editor content scrollable */
        .quill-root {
          height: 100%;
          min-height: 200px;
          display: flex;
          flex-direction: column;
        }
        .quill-root .ql-toolbar {
          position: sticky;
          top: 0;
          z-index: 3;
        }

        .ql-toolbar {
          border: 0 !important;
        }

        .quill-root .ql-container {
          flex: 1 1 auto;
          overflow: auto;
          min-height: 0;
        }

        .ql-container {
          border: 0px !important;
          border-top: 1px solid ${gray[200]} !important;
        }
      `}</style>
    </Box>
  );
};

export default QuillEditor;
